import { useState, useEffect, useCallback } from 'react';
import { analysisApi, InflowAnalysisParams } from '@/api/analysis';
import type { InflowAnalysisResponse } from '@/types/data';
import { useAuthStore } from '@/stores/authStore';

// シナリオごとの色
const SCENARIO_COLORS = [
  '#3B82F6', // blue
  '#10B981', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#84CC16', // lime
  '#F97316', // orange
  '#6366F1', // indigo
];

export default function InflowAnalysis() {
  const { accessibleCompanies } = useAuthStore();
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [result, setResult] = useState<InflowAnalysisResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 初期化
  useEffect(() => {
    if (accessibleCompanies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(accessibleCompanies[0].id);
    }
  }, [accessibleCompanies, selectedCompanyId]);

  // データ取得
  const fetchData = useCallback(async () => {
    if (!selectedCompanyId) return;
    setIsLoading(true);
    setError(null);
    try {
      const params: InflowAnalysisParams = {
        company_id: selectedCompanyId,
        start_date: startDate,
        end_date: endDate,
      };
      const data = await analysisApi.getInflowAnalysis(params);
      setResult(data);
    } catch (err) {
      console.error('流入経路分析エラー:', err);
      setError('データの取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, [selectedCompanyId, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 円グラフのSVGパス生成
  const generatePieChart = (data: { scenario: string; percentage: number }[]) => {
    if (data.length === 0) return null;

    const cx = 100;
    const cy = 100;
    const radius = 80;
    let currentAngle = -90; // 上から開始

    const paths = data.map((item, index) => {
      const angle = (item.percentage / 100) * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;

      // 小さすぎるセグメントはスキップ
      if (angle < 1) {
        currentAngle = endAngle;
        return null;
      }

      const startRad = (startAngle * Math.PI) / 180;
      const endRad = (endAngle * Math.PI) / 180;

      const x1 = cx + radius * Math.cos(startRad);
      const y1 = cy + radius * Math.sin(startRad);
      const x2 = cx + radius * Math.cos(endRad);
      const y2 = cy + radius * Math.sin(endRad);

      const largeArcFlag = angle > 180 ? 1 : 0;

      const pathData = [
        `M ${cx} ${cy}`,
        `L ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        'Z',
      ].join(' ');

      currentAngle = endAngle;

      return (
        <path
          key={item.scenario}
          d={pathData}
          fill={SCENARIO_COLORS[index % SCENARIO_COLORS.length]}
          stroke="white"
          strokeWidth="2"
        />
      );
    });

    return (
      <svg viewBox="0 0 200 200" className="w-48 h-48">
        {paths}
      </svg>
    );
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6">流入経路分析</h1>

      {/* フィルター */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">法人</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={selectedCompanyId || ''}
              onChange={(e) => setSelectedCompanyId(Number(e.target.value))}
            >
              <option value="">選択してください</option>
              {accessibleCompanies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.display_name || company.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">開始日</label>
            <input
              type="date"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">終了日</label>
            <input
              type="date"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="flex items-end">
            <button
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
              onClick={fetchData}
              disabled={isLoading || !selectedCompanyId}
            >
              {isLoading ? '読込中...' : '更新'}
            </button>
          </div>
        </div>
      </div>

      {/* エラー表示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* 結果表示 */}
      {result && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* サマリーカード */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">サマリー</h2>
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-600">期間</div>
                <div className="font-medium">
                  {result.period.start_date} 〜 {result.period.end_date}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">総送信数</div>
                <div className="text-3xl font-bold text-blue-600">
                  {result.grand_total.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">経路数</div>
                <div className="text-xl font-semibold">{result.data.length}件</div>
              </div>
            </div>
          </div>

          {/* 円グラフ */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">構成比</h2>
            {result.data.length === 0 ? (
              <div className="text-center text-gray-500 py-8">データがありません</div>
            ) : (
              <div className="flex flex-col items-center">
                {generatePieChart(result.data)}
                {/* 凡例 */}
                <div className="mt-4 grid grid-cols-2 gap-2 w-full">
                  {result.data.slice(0, 6).map((item, index) => (
                    <div key={item.scenario} className="flex items-center gap-2 text-sm">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: SCENARIO_COLORS[index % SCENARIO_COLORS.length] }}
                      />
                      <span className="truncate">{item.scenario}</span>
                      <span className="text-gray-500 ml-auto">{item.percentage}%</span>
                    </div>
                  ))}
                  {result.data.length > 6 && (
                    <div className="text-sm text-gray-500 col-span-2">
                      他 {result.data.length - 6} 件
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* 詳細テーブル */}
          <div className="bg-white rounded-lg shadow p-6 lg:col-span-1">
            <h2 className="text-lg font-semibold mb-4">経路別詳細</h2>
            {result.data.length === 0 ? (
              <div className="text-center text-gray-500 py-8">データがありません</div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {result.data.map((item, index) => (
                  <div
                    key={item.scenario}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: SCENARIO_COLORS[index % SCENARIO_COLORS.length] }}
                      />
                      <span className="font-medium">{item.scenario}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{item.daily_total.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">{item.percentage}%</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 詳細テーブル（全幅） */}
      {result && result.data.length > 0 && (
        <div className="bg-white rounded-lg shadow mt-6">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">流入経路一覧</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    順位
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    シナリオ
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    日次合計
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    累計（最新）
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    構成比
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    データ数
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {result.data.map((item, index) => (
                  <tr key={item.scenario} className={index < 3 ? 'bg-yellow-50' : ''}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-sm font-medium ${
                          index === 0
                            ? 'bg-yellow-500 text-white'
                            : index === 1
                            ? 'bg-gray-400 text-white'
                            : index === 2
                            ? 'bg-orange-400 text-white'
                            : 'bg-gray-200 text-gray-600'
                        }`}
                      >
                        {index + 1}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{
                            backgroundColor: SCENARIO_COLORS[index % SCENARIO_COLORS.length],
                          }}
                        />
                        <span className="font-medium">{item.scenario}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right font-semibold">
                      {item.daily_total.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-gray-600">
                      {item.cumulative_latest.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div
                            className="h-2 rounded-full"
                            style={{
                              width: `${item.percentage}%`,
                              backgroundColor: SCENARIO_COLORS[index % SCENARIO_COLORS.length],
                            }}
                          />
                        </div>
                        <span className="text-sm font-medium w-12 text-right">
                          {item.percentage}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-gray-500">
                      {item.data_points}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-100">
                <tr>
                  <td className="px-4 py-3 font-semibold" colSpan={2}>
                    合計
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-blue-600">
                    {result.grand_total.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right">-</td>
                  <td className="px-4 py-3 text-right font-semibold">100%</td>
                  <td className="px-4 py-3 text-right text-gray-500">
                    {result.data.reduce((sum, item) => sum + item.data_points, 0)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* データなし */}
      {result && result.data.length === 0 && !isLoading && (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          該当するデータがありません。期間や法人を変更してお試しください。
        </div>
      )}
    </div>
  );
}
